import { z } from 'zod'
import { monosanFetch } from '@/shared/api/monosanFetch'
import {
  aesCbcEncryptRaw,
  generateAesKeyAndIv,
  hmacSha256Hex,
  wrapKeyMaterial,
} from '@/features/transfer/crypto/paymentSecurity'
import { TRANSFER_CURRENCY } from '../model/constants'
import type { TransferFormData } from '../model/types'
import { parseAmount } from '../model/validation'

const encryptionKeySchema = z.object({ publicKey: z.string() })
const initiatePaymentResponseSchema = z.object({ paymentId: z.string() })

export type InitiatePaymentResult = z.infer<typeof initiatePaymentResponseSchema>

// Cerium's RSA public key, fetched fresh for every payment attempt.
export const getEncryptionKey = async (): Promise<string> => {
  const { publicKey } = await monosanFetch('/api/encryption/key', {
    schema: encryptionKeySchema,
  })
  return publicKey
}

export const initiatePayment = async (
  data: TransferFormData,
  pin: string
): Promise<InitiatePaymentResult> => {
  const pemPublicKey = await getEncryptionKey()
  const { key, iv } = await generateAesKeyAndIv()
  const nonceToken = crypto.randomUUID()

  const [encryptedPin, encryptedNonce, rawKey] = await Promise.all([
    aesCbcEncryptRaw(pin, key, iv),
    aesCbcEncryptRaw(nonceToken, key, iv),
    crypto.subtle.exportKey('raw', key),
  ])
  const encryptedSecret = await wrapKeyMaterial(rawKey, iv, pemPublicKey)

  const amount = parseAmount(data.amount).toFixed(2)
  // Not sent to the server (PaymentConstants.CURRENCY is enforced there),
  // but must match it exactly — the hash is verified server-side using the
  // same constant.
  const currency: string = TRANSFER_CURRENCY
  const creditorName = data.name
  const referenceText = data.reference || ''
  const paymentHash = await hmacSha256Hex(
    nonceToken,
    `${data.iban}|${amount}|${currency}|${creditorName}|${referenceText}`
  )

  return monosanFetch('/api/payment', {
    method: 'POST',
    headers: {
      'encrypted-pin': encryptedPin,
      'encrypted-secret': encryptedSecret,
      'encrypted-nonce': encryptedNonce,
    },
    body: {
      beneficiary: { name: creditorName, iban: data.iban },
      amount,
      referenceText,
      paymentHash,
    },
    schema: initiatePaymentResponseSchema,
  })
}

export const confirmPaymentOtp = async (
  paymentId: string,
  otpCode: string
): Promise<void> => {
  await monosanFetch(`/api/payment/${encodeURIComponent(paymentId)}`, {
    method: 'PUT',
    body: { otpCode },
    schema: z.unknown(),
  })
}
